import React, { useState } from 'react';
import DataTable, {
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@amzn/meridian/data-table';
import { type SortDirection, type OnSort } from '@amzn/meridian/data-table/types/column-sort';
import Row from '@amzn/meridian/row';
import Image from '@amzn/meridian/image';
import Checkbox from '@amzn/meridian/checkbox';
import { useTranslation } from 'src/arb-wrapper';
import { BenderAssetType, FEATURE_FLAGS } from 'src/constants';
import { isFeatureEnabled } from 'src/feature-flags';
import {
  type CampaignDesiredStateDto,
  type AssetDto,
} from 'src/store/generated/leela-api-generated';
import { sanitizeMeridianImageSrc } from 'src/utils';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import { extractFilenameFromAsset } from 'src/utils/AssetUtils';
import { shouldShowAdspStvWarning } from './AssetCard';
import Tooltip from '@amzn/meridian/tooltip';
import errorTokens from '@amzn/meridian-tokens/base/icon/error';
import { PublisherDisplay } from 'src/components/common/PublisherDisplay';
import { assetStrategyKey, isSameAssetStrategy } from 'src/utils/assetKeys';
import { resolveStrategyLabel } from 'src/utils/strategyLabels';
import { formatUtcDate } from 'src/utils/formatDateTime';

import { AssetSourceTag } from './AssetSourceTag';
import { RELEASE_DATE_FALLBACK, ReleaseDateSkeleton } from './release-date-display';

function getReleaseDateDisplayValue (
  titles: AssetDto['titles'],
  releaseDatesByGti: ReadonlyMap<string, string | undefined> | undefined,
): string {
  if (titles.length === 0) return RELEASE_DATE_FALLBACK;
  return titles
    .map(({ gti }) => formatUtcDate(releaseDatesByGti?.get(gti), RELEASE_DATE_FALLBACK))
    .join(', ');
}

export interface AssetTableProps {
  assets: (object[] & AssetDto[]) | undefined;
  campaigns: (object[] & CampaignDesiredStateDto[]) | undefined;
  selectedAssets: AssetDto[];
  releaseDatesByGti?: ReadonlyMap<string, string | undefined>;
  isReleaseDatesFetching?: boolean;
  sortColumn?: string;
  sortDirection?: SortDirection;
  handleAssetSelect: (asset: AssetDto) => void;
  setAssetDetailView: (assetDetailView: boolean) => void;
  setAssetDetail: (asset: AssetDto) => void;
  setSelectedAssets: (assets: AssetDto[]) => void;
  onSortChange: (sortColumn: string, sortDirection: 'ascending' | 'descending') => void;
  // productStrategyId → display name, shown in the Strategy column.
  strategyLabels?: Map<string, string>;
}
export const AssetTable = ({
  assets,
  campaigns,
  selectedAssets,
  releaseDatesByGti,
  isReleaseDatesFetching,
  handleAssetSelect,
  setAssetDetailView,
  setAssetDetail,
  onSortChange,
  sortColumn,
  sortDirection,
  strategyLabels,
}: AssetTableProps) => {
  const { t } = useTranslation();
  const isReleaseDateEnabled = isFeatureEnabled(
    FEATURE_FLAGS.ENABLE_ASSET_IMPROVEMENTS,
  );
  const columnCount = isReleaseDateEnabled ? 9 : 8;
  // Each row is already one (asset × strategy) from the server grouping — no client-side merging.
  const rows = assets ?? [];

  const onSort: OnSort = ({ sortColumn, sortDirection }) => {
    if (sortDirection !== 'none') {
      onSortChange(sortColumn, sortDirection);
    }
  };

  const getThumbnailFromOptions = (asset: AssetDto) => {
    if (!asset.thumbnailOptions?.length) {
      return '';
    }

    // Find the first approved thumbnail
    const approvedThumbnail = asset.thumbnailOptions.find(
      option => option.approvalStatus === 'APPROVED'
    );

    return approvedThumbnail?.assetUrl ?? asset.thumbnailOptions[0].assetUrl;
  };

  return (
    <DataTable
      data-testid="asset-table"
      showBorder={true}
      showRowDividers={true}
      size="medium"
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      stickyHeaderRows={true}
      width="100%"
      onSort={onSort}
    >
      <DataTableHead backgroundColor="secondary">
        <DataTableRow>
          <DataTableHeader width={48}></DataTableHeader>
          <DataTableHeader data-testid="asset-header-titles" sortColumn="titles" width={280}>
            {t('asset_detail_view_header_asset_title')}
          </DataTableHeader>
          {isReleaseDateEnabled && (
            <DataTableHeader width={120}>
              {t('asset_detail_view_header_release_date')}
            </DataTableHeader>
          )}
          <DataTableHeader width={200}>{t('asset_detail_view_header_filename')}</DataTableHeader>
          <DataTableHeader width={200}>{t('asset_detail_view_header_campaigns')}</DataTableHeader>
          <DataTableHeader width={200}>
            {t('asset_detail_view_header_strategies')}
          </DataTableHeader>
          <DataTableHeader width={100}>
            {t('asset_detail_view_header_source')}
          </DataTableHeader>
          <DataTableHeader data-testid="asset-header-locales" sortColumn="locale" width={100}>
            {t('asset_detail_view_header_locale')}
          </DataTableHeader>
          <DataTableHeader sortColumn="assetType" width={100}>
            {t('asset_detail_view_header_asset_type')}
          </DataTableHeader>
        </DataTableRow>
      </DataTableHead>
      <DataTableBody>
      {rows.length === 0 && (
        <DataTableRow>
          <DataTableCell colSpan={columnCount}>{t('table_no_results')}</DataTableCell>
        </DataTableRow>
      )}
      {rows
        .map((asset: AssetDto) => {
          const rowKey = assetStrategyKey(asset);
          const isSelected = selectedAssets.some((a) => isSameAssetStrategy(a, asset));
          return (
          <DataTableRow
            data-testid={`asset-table-row-${asset.assetId}`}
            key={rowKey}
          >
            <DataTableCell>
              <Checkbox
                checked={isSelected}
                data-testid={`asset-table-checkbox-${asset.assetId}`}
                value={isSelected}
                onChange={() => {
                  handleAssetSelect(asset);
                }}
              />
            </DataTableCell>
            <DataTableCell>
              <Row>
                <div
                  data-testid={`asset-thumbnail-${asset.assetId}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setAssetDetail(asset);
                    setAssetDetailView(true);
                  }}>
                  <div style={{ width: '64px', height: '64px' }}>
                    {asset.assetType === BenderAssetType.VIDEO
                      ? (
                      <Image
                        size="contain"
                        src={sanitizeMeridianImageSrc(
                          getThumbnailFromOptions(asset),
                        )}
                        viewportAspectRatio={1}
                      />
                        )
                      : (
                      <Image
                        size="contain"
                        src={sanitizeMeridianImageSrc(asset.assetUrl)}
                        viewportAspectRatio={1}
                        />
                        )}
                  </div>
                </div>
                <Column spacing="200">
                  <Button
                    data-testid={`asset-detail-button-${asset.assetId}`}
                    size="medium"
                    type="link"
                    onClick={() => {
                      setAssetDetail(asset);
                      setAssetDetailView(true);
                    }}
                  >
                    {(() => {
                      const fullText = asset.titles.map((title) => title.name).join(', ');
                      return fullText.length > 30 ? `${fullText.substring(0, 30)}...` : fullText;
                    })()}
                  </Button>
                </Column>
              </Row>
            </DataTableCell>
            {isReleaseDateEnabled && (
              <DataTableCell data-testid={`asset-table-release-date-${asset.assetId}`}>
                {isReleaseDatesFetching
                  ? <ReleaseDateSkeleton testId={`asset-table-release-date-skeleton-${asset.assetId}`} />
                  : getReleaseDateDisplayValue(asset.titles, releaseDatesByGti)}
              </DataTableCell>
            )}
            <DataTableCell>
              <div style={{ maxWidth: 200 }}><Text breakWord={true}>{extractFilenameFromAsset(asset, t)}</Text></div>
            </DataTableCell>
            <DataTableCell>
              {campaigns && (
                <CampaignIdList
                  campaignIds={asset.campaignIds}
                  campaigns={campaigns}
                />
              )}
            </DataTableCell>
            <DataTableCell>
              <div style={{ maxWidth: 200, wordBreak: 'break-word' }}>
                <Text breakWord={true} data-testid={`asset-strategy-${asset.assetId}`}>
                  {[resolveStrategyLabel(strategyLabels, asset.productStrategyId), '—'].find(Boolean)}
                </Text>
              </div>
            </DataTableCell>
            <DataTableCell>
              <AssetSourceTag
                generationMethods={asset.metadata?.generationMethods}
              />
            </DataTableCell>
            <DataTableCell>{asset.locale}</DataTableCell>
            <DataTableCell>
              {asset.assetType} { }
              {shouldShowAdspStvWarning(asset) && (
                <Tooltip id="adsp-video-message" position="end" title={t('adsp_video_stv_warning')}>
                  <Icon color="error" data-testid="adsp-stv-warning-icon" tokens={errorTokens} />
                </Tooltip>
              )}
            </DataTableCell>
          </DataTableRow>
          );
        })}
      </DataTableBody>
    </DataTable>
  );
};

const CAMPAIGN_DISPLAY_LIMIT = 5;

export const CampaignIdList = (props: {
  campaignIds: string[];
  campaigns: object[] & CampaignDesiredStateDto[];
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hasMore = props.campaignIds.length > CAMPAIGN_DISPLAY_LIMIT;
  const visibleIds = expanded ? props.campaignIds : props.campaignIds.slice(0, CAMPAIGN_DISPLAY_LIMIT);

  return (
    <Column>
      {visibleIds.map((campaignId: string) => {
        const campaign = props.campaigns.find(
          (campaign: CampaignDesiredStateDto) => {
            return String(campaign.campaignId) === String(campaignId);
          },
        );

        return (
          <Row key={campaignId} spacing="200">
            <PublisherDisplay publisher={campaign?.publisher ?? ''} spacing="100" textType="b200" />
            <Text type="b300">{campaignId}</Text>
          </Row>
        );
      })}
      {hasMore && (
        <Button
          data-testid="campaign-list-toggle"
          size="small"
          type="link"
          onClick={() => { setExpanded(prev => !prev); }}
        >
          {expanded
            ? t('show_less')
            : t('show_more_campaigns', { count: props.campaignIds.length - CAMPAIGN_DISPLAY_LIMIT })}
        </Button>
      )}
    </Column>
  );
};
